import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Plus, Trash2, TrendingUp, TrendingDown, PiggyBank, Calculator, Pencil, X, Check } from "lucide-react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

import { API } from '../utils/api';

const INCOME_CATEGORIES = [
  "Annual Bonus",
  "13th Month Salary",
  "Tax Refund",
  "Investment Dividend",
  "Other Income",
];

const EXPENSE_CATEGORIES = [
  "Annual Insurance",
  "School Fees",
  "Sports Membership",
  "Vacation",
  "Property Tax",
  "Car Maintenance",
  "Home Maintenance",
  "Medical Checkup",
  "Other Expenses",
];

const formatCurrency = (value, currency = "EUR") => {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
  }).format(value);
};

export default function AnnualItems({ user }) {
  const [data, setData] = useState({ 
    items: [], 
    monthly_net_balance: 0,
    monthly_net_balance_annual: 0,
    annual_income_items: 0,
    total_income: 0, 
    total_expenses: 0, 
    annual_savings: 0 
  });
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({
    description: "",
    amount: "",
    item_type: "expense",
    category: "",
  });

  useEffect(() => {
    fetchBudget();
  }, []);

  const fetchBudget = async () => {
    try {
      const response = await axios.get(`${API}/annual-budget`);
      setData(response.data);
    } catch (error) {
      console.error("Error fetching budget:", error);
      toast.error("Failed to load annual budget");
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async () => {
    if (!newItem.category || !newItem.amount) {
      toast.error("Please fill in category and amount");
      return;
    }

    try {
      await axios.post(`${API}/annual-budget`, {
        description: newItem.description || newItem.category,
        amount: parseFloat(newItem.amount),
        item_type: newItem.item_type,
        category: newItem.category,
      });
      
      toast.success("Item added");
      setNewItem({ description: "", amount: "", item_type: "expense", category: "" });
      fetchBudget();
    } catch (error) {
      console.error("Error adding item:", error);
      toast.error("Failed to add item");
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await axios.delete(`${API}/annual-budget/${itemId}`);
      toast.success("Item deleted");
      fetchBudget();
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error("Failed to delete item");
    }
  };

  const handleEditItem = (item) => {
    setEditingItem({
      ...item,
      amount: item.amount.toString(),
    });
  };

  const handleUpdateItem = async () => {
    if (!editingItem.category || !editingItem.amount) {
      toast.error("Please fill in category and amount");
      return;
    }

    try {
      await axios.put(`${API}/annual-budget/${editingItem.item_id}`, {
        description: editingItem.description || editingItem.category,
        amount: parseFloat(editingItem.amount),
        item_type: editingItem.item_type,
        category: editingItem.category,
      });
      
      toast.success("Item updated");
      setEditingItem(null);
      fetchBudget();
    } catch (error) {
      console.error("Error updating item:", error);
      toast.error("Failed to update item");
    }
  };

  const cancelEdit = () => {
    setEditingItem(null);
  };

  const currency = user?.default_currency || "EUR";
  const categories = newItem.item_type === "income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;

  const incomeItems = data.items.filter(item => item.item_type === "income");
  const expenseItems = data.items.filter(item => item.item_type === "expense");

  if (loading) {
    return (
      <Layout user={user} pageTitle="Annual Budget">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} pageTitle="Annual Budget">
      <div data-testid="annual-items-page" className="space-y-8">
        {/* Header */}
        <div>
          <p className="text-gray-400">
            Define your annual income and expenses (bonus, insurance, vacation, etc.). Annual Savings will be used in Future Planning.
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Net Balance × 12 (Auto-calculated) */}
          <Card className="bg-card border-border border-blue-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <Calculator className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Net Balance × 12</p>
                  <p className="text-xl font-bold text-blue-400 number-display">
                    {formatCurrency(data.monthly_net_balance_annual, currency)}
                  </p>
                  <p className="text-xs text-gray-500">
                    ({formatCurrency(data.monthly_net_balance, currency)}/mo)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Other Annual Income */}
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Other Annual Income</p>
                  <p className="text-xl font-bold text-green-400 number-display">
                    {formatCurrency(data.annual_income_items, currency)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Annual Expenses */}
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Annual Expenses</p>
                  <p className="text-xl font-bold text-red-400 number-display">
                    {formatCurrency(data.total_expenses, currency)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Annual Savings (for Future Planning) */}
          <Card className="bg-card border-border border-purple-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                  <PiggyBank className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Annual Savings</p>
                  <p className={`text-xl font-bold number-display ${data.annual_savings >= 0 ? 'text-purple-400' : 'text-red-400'}`}>
                    {formatCurrency(data.annual_savings, currency)}
                  </p>
                  <p className="text-xs text-gray-500">→ Future Planning</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Formula explanation */}
        <Card className="bg-secondary/50 border-border">
          <CardContent className="p-4">
            <p className="text-sm text-gray-400 text-center">
              <span className="text-blue-400">Net Balance × 12</span>
              {" + "}
              <span className="text-green-400">Other Annual Income</span>
              {" - "}
              <span className="text-red-400">Annual Expenses</span>
              {" = "}
              <span className="text-purple-400 font-bold">Annual Savings</span>
            </p>
            <p className="text-xs text-gray-500 text-center mt-1">
              Net Balance = Monthly Income - Monthly Expenses - Monthly Savings
            </p>
          </CardContent>
        </Card>

        {/* Add New Item */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-bold text-white">Add Annual Item</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <Select
                value={newItem.item_type}
                onValueChange={(value) => setNewItem({ ...newItem, item_type: value, category: "" })}
              >
                <SelectTrigger className="bg-secondary border-border text-white">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="income" className="text-gray-300 focus:text-white focus:bg-secondary">Income</SelectItem>
                  <SelectItem value="expense" className="text-gray-300 focus:text-white focus:bg-secondary">Expense</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={newItem.category}
                onValueChange={(value) => setNewItem({ ...newItem, category: value })}
              >
                <SelectTrigger className="bg-secondary border-border text-white">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat} className="text-gray-300 focus:text-white focus:bg-secondary">
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Input
                placeholder="Description (optional)"
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                className="bg-secondary border-border text-white placeholder:text-gray-500"
              />

              <Input
                type="number"
                placeholder={`Amount (${currency})`}
                value={newItem.amount}
                onChange={(e) => setNewItem({ ...newItem, amount: e.target.value })}
                className="bg-secondary border-border text-white placeholder:text-gray-500"
                data-testid="amount-input"
              />

              <Button
                onClick={handleAddItem}
                className="bg-green-500 hover:bg-green-400 text-black rounded-full font-semibold"
                data-testid="add-item-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Income and Expenses */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Income */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg font-bold text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-400" />
                Other Annual Income
              </CardTitle>
            </CardHeader>
            <CardContent>
              {incomeItems.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No annual income items yet (bonus, dividends, etc.)</p>
              ) : (
                <div className="space-y-3">
                  {incomeItems.map((item) => (
                    <motion.div
                      key={item.item_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center justify-between p-3 bg-secondary rounded-xl"
                    >
                      {editingItem?.item_id === item.item_id ? (
                        <>
                          <div className="flex-1 grid grid-cols-3 gap-2 mr-2">
                            <Input
                              value={editingItem.description}
                              onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                              placeholder="Description"
                              className="bg-background border-border text-white text-sm"
                            />
                            <Select
                              value={editingItem.category}
                              onValueChange={(value) => setEditingItem({ ...editingItem, category: value })}
                            >
                              <SelectTrigger className="bg-background border-border text-white text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-card border-border">
                                {INCOME_CATEGORIES.map((cat) => (
                                  <SelectItem key={cat} value={cat} className="text-gray-300 focus:text-white focus:bg-secondary">
                                    {cat}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Input
                              type="number"
                              value={editingItem.amount}
                              onChange={(e) => setEditingItem({ ...editingItem, amount: e.target.value })}
                              className="bg-background border-border text-white text-sm"
                            />
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-green-500 hover:text-green-400"
                              onClick={handleUpdateItem}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-gray-500 hover:text-gray-400"
                              onClick={cancelEdit}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <p className="font-medium text-white">{item.description || item.category}</p>
                            <p className="text-sm text-gray-500">{item.category}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-green-400 number-display">
                              +{formatCurrency(item.amount, currency)}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-gray-500 hover:text-blue-400"
                              onClick={() => handleEditItem(item)}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-gray-500 hover:text-red-400"
                              onClick={() => handleDeleteItem(item.item_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Expenses */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg font-bold text-white flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-red-400" />
                Annual Expenses
              </CardTitle>
            </CardHeader>
            <CardContent>
              {expenseItems.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No annual expense items yet (insurance, school fees, etc.)</p>
              ) : (
                <div className="space-y-3">
                  {expenseItems.map((item) => (
                    <motion.div
                      key={item.item_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center justify-between p-3 bg-secondary rounded-xl"
                    >
                      {editingItem?.item_id === item.item_id ? (
                        <>
                          <div className="flex-1 grid grid-cols-3 gap-2 mr-2">
                            <Input
                              value={editingItem.description}
                              onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                              placeholder="Description"
                              className="bg-background border-border text-white text-sm"
                            />
                            <Select
                              value={editingItem.category}
                              onValueChange={(value) => setEditingItem({ ...editingItem, category: value })}
                            >
                              <SelectTrigger className="bg-background border-border text-white text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-card border-border">
                                {EXPENSE_CATEGORIES.map((cat) => (
                                  <SelectItem key={cat} value={cat} className="text-gray-300 focus:text-white focus:bg-secondary">
                                    {cat}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Input
                              type="number"
                              value={editingItem.amount}
                              onChange={(e) => setEditingItem({ ...editingItem, amount: e.target.value })}
                              className="bg-background border-border text-white text-sm"
                            />
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-green-500 hover:text-green-400"
                              onClick={handleUpdateItem}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-gray-500 hover:text-gray-400"
                              onClick={cancelEdit}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <p className="font-medium text-white">{item.description || item.category}</p>
                            <p className="text-sm text-gray-500">{item.category}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-red-400 number-display">
                              -{formatCurrency(item.amount, currency)}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-gray-500 hover:text-blue-400"
                              onClick={() => handleEditItem(item)}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-gray-500 hover:text-red-400"
                              onClick={() => handleDeleteItem(item.item_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
